<template>
  <v-row no-gutters class="tb-view-area">
    <v-col md="10">
      <v-menu
        offset-y
        transition="scroll-y-transition"
        content-class="tb-view-menu"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-on="on" v-bind="attrs" class="tb-view-menu-btn">
            <v-icon>mdi mdi-format-list-numbered-rtl</v-icon>
          </v-btn>
        </template>

        <v-list>
          <div class="tb-view-menu__label">Views</div>
          <v-list-item
            class="tb-view-menu__item"
            :class="{ 'tb-view-menu__item--active': activeViewId === 'all' }"
            @click="goToView('all')"
          >
            <v-list-item-title>All</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-for="(view, index) in listAllViews"
            :key="index"
            class="tb-view-menu__item"
            :class="{ 'tb-view-menu__item--active': isActiveView(view.id) }"
            @click="goToView(view.id)"
          >
            <v-icon v-if="view.isPrivate === true" left size="14">
              mdi-lock
            </v-icon>
            <v-list-item-title>{{ view.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-tabs
        center-active
        show-arrows
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
        v-model="selectedViewTab"
        :color="HRDC_BLUE_THEME_COLOR"
      >
        <v-tab @click="goToView('trashed')"> Trashed </v-tab>

        <v-tab @click="goToView('all')">All</v-tab>
        <v-tab
          v-for="(view, index) in listAllViews"
          :key="view.id"
          :tabindex="index + 2"
          @click="goToView(view.id)"
        >
          <v-icon left v-if="view.isPrivate === true" size="small">
            mdi-lock
          </v-icon>
          {{ view.name }}

          <div class="text-center tb-view-dropdown-btn">
            <v-menu open-on-click bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on">
                  <span class="mdi mdi-chevron-down"></span>
                </v-btn>
              </template>

              <v-list>
                <v-list-item @click="openViewNameDialog(view.id, view.name)">
                  <v-list-item-title>Rename</v-list-item-title>
                </v-list-item>
                <template v-if="currentUserID === view.ownerUuid">
                  <v-list-item
                    @click="setPrivateOrPublic(view.id, view.isPrivate)"
                  >
                    <v-list-item-title v-if="view.isPrivate === true"
                      >Set as Public</v-list-item-title
                    >
                    <v-list-item-title v-else>Set as Private</v-list-item-title>
                  </v-list-item>
                </template>
                <v-list-item @click="openConfirmationDialog(view.id)">
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-tab>
      </v-tabs>
    </v-col>

    <v-col md="2" align="right">
      <v-btn tile text outlined @click="saveNewView" elevation="0"
        >New View</v-btn
      >
      <v-btn
        tile
        text
        outlined
        :disabled="selectedTab === 'all' || selectedTab === 'trashed'"
        @click="updateView"
        elevation="0"
        >Save</v-btn
      >
      <v-dialog
        max-width="440"
        v-model="renameEachViewDialog"
        content-class="rename-view-dialog"
      >
        <template v-slot:default="dialog">
          <v-card class="rename-card">
            <div class="rename-title">Rename View</div>
            <div class="rename-subtitle">
              Update the name of your selected view
            </div>
            <v-text-field
              v-model="selectedViewName"
              outlined
              dense
              hide-details
              placeholder="Enter view name"
              class="rename-input"
            ></v-text-field>
            <div class="rename-actions">
              <v-btn
                class="rename-btn rename-btn--cancel"
                depressed
                @click="dialog.value = false"
                >Cancel</v-btn
              >
              <v-btn
                class="rename-btn rename-btn--confirm"
                depressed
                @click="renameViewName(selectedViewId, selectedViewName)"
                >Update</v-btn
              >
            </div>
          </v-card>
        </template>
      </v-dialog>

      <TheModalConfirmation
        ref="confirmPopup"
        @agree="agreeCallback"
        @disagree="disagreeCallback"
      ></TheModalConfirmation>
    </v-col>
  </v-row>
</template>

<script>
import Api from "@/objects/api";
import { writeColumnCondition } from "./breadDataTable/columnCondition";
import { mapState } from "vuex";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc/objects/globalData";
import TheModalConfirmation from "./TheModalConfirmation.vue";

export default {
  name: "BreadTabulatorView",
  mixins: [hrdcFunction],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
      services: (state) => state.service.data.services,
    }),
    // Which entry the views dropdown should mark as current. Read from the URL
    // (not `selectedTab`) because the page remounts on every ?viewId change, so
    // the query is the one source that is always in step with what is rendered.
    activeViewId() {
      return String(this.$route.query.viewId || "all");
    },
  },
  components: {
    TheModalConfirmation,
  },
  props: {
    listAllViews: {
      type: Array,
    },
    model: {
      type: Object,
      required: true,
    },
    selectedCustomColums: {},
    selectedAdvanceFilterConditions: {},
    selectedFilterConditions: {},
    cColumnSort: {},
    displayRowCount: {},
    allResizeColumns: {},
  },
  data: () => ({
    isPrivate: true,
    api: new Api(),
    hrdcData: new HrdcData(),
    renameEachViewDialog: false,
    selectedViewName: null,
    selectedViewId: null,
    confirmationTitle: "Confirmation",
    confirmationMessage: "Are you sure you want to delete this view?",
    currentUserID: "",
    selectedTab: "all",
    selectedViewTab: 1,
  }),
  methods: {
    isActiveView(viewId) {
      return this.activeViewId === `${viewId}`;
    },
    goToView(viewId) {
      // Reset stored pagination so the target view starts on page 1.
      this.$store.commit("assignDeveloperData", {
        paginationPageNo: null,
      });

      // Re-clicking the tab that is already active must still refresh the table
      // (regression: this used to re-apply the view in place). Navigating to the
      // same ?viewId is a no-op so the keyed router-view never remounts. Worse,
      // a refresh-token remount briefly shows the default tab ("All") before the
      // async getAllViews resolves, which blinks. So for the same view, refresh
      // in place via the changeTableDetails emit (no navigation, no blink); the
      // parent re-applies the view and reloads the data.
      if (String(viewId) === String(this.$route.query.viewId)) {
        this.$emit("changeTableDetails", this.listAllViews, viewId);
        return;
      }

      // Different view: navigate only. Changing ?viewId remounts the page (the
      // console layout's router-view is keyed on $route.fullPath), and the fresh
      // mount applies the view exactly once via getAllViews -> changeTableDetails.
      // This avoids running the data-loading cascade twice (once on the dying
      // instance, once on the remounted one).
      this.loadSpecificViewData(viewId);
    },
    changeSelectedTabStatus(viewId, tabIndex) {
      this.selectedTab = viewId;
      this.selectedViewTab =
        viewId === "all" ? 1 : viewId === "trashed" ? 0 : tabIndex;
      this.loadSpecificViewData(viewId);
    },
    saveNewView() {
      //console.log("selectedCustomColums");
      this.api.setMethod(`POST`);
      this.api.setUrl(`${this.$service.hrdc}/v1/en/console/crmView/add`);
      this.api.setParams({
        // Store the view under the SAME modelKey the page lists views by, so it
        // shows up in that module's tab strip. Event modules (Tax Summit, Stamp
        // Duty, ...) list by `crmViewKey` (e.g. "tax_summit_application"); every
        // other page — regular hrdc application and the generic browse pages —
        // lists by `key`. crmViewKey is only ever set on event models, so this
        // falls back to `key` everywhere else. Must mirror getAllViews.
        modelKey: this.model.crmViewKey || this.model.key,
        ownerUuid: this.auth.uuid,
        cColumnCondition: writeColumnCondition(this.selectedCustomColums),
        aFilterCondition: JSON.stringify([]),
        filterCondition: JSON.stringify([]),
        cColumnSort: JSON.stringify([]),
        displayRowCount: 10,
        isPrivate: this.isPrivate,
        manageColumnCustomWidth: JSON.stringify([]),
      });
      this.api.setCallbackCompleted((response) => {
        let viewId = response.id;
        //let tabIndex = response.activeCount;
        // Point the URL at the new view, then hard-reload so the tab strip
        // re-fetches (getAllViews) and selects/scrolls to it. Same pattern as
        // updateView / renameViewName / deleteView / setPrivateOrPublic — the
        // soft navigation alone did not surface the newly created tab.
        this.loadSpecificViewData(viewId);

        this.$store.dispatch("showMessage", {
          message: "New View Created successfully.",
          messageType: "success",
          timeout: 2000,
        });

        this.$router.go();
      });
      this.api.fetch();
    },
    updateView() {
      const viewId = this.$route.query.viewId;

      this.api.setMethod(`POST`);
      this.api.setUrl(`${this.$service.hrdc}/v1/en/console/crmView/edit`);
      this.api.setParams({
        id: viewId,
        cColumnCondition: writeColumnCondition(this.selectedCustomColums),
        aFilterCondition: JSON.stringify(this.selectedAdvanceFilterConditions),
        filterCondition: JSON.stringify(this.selectedFilterConditions),
        cColumnSort: JSON.stringify(this.cColumnSort),
        displayRowCount: this.displayRowCount,
        isPrivate: this.isPrivate,
        manageColumnCustomWidth: JSON.stringify(this.allResizeColumns),
      });
      this.api.setCallbackCompleted(() => {
        this.$store.dispatch("showMessage", {
          message: "View Updated successfully.",
          messageType: "success",
          timeout: 2000,
        });
        this.$router.go();
      });
      this.api.fetch();
    },
    renameViewName(viewId, viewName) {
      // call API to update the view name
      this.api.setMethod(`POST`);
      this.api.setUrl(`${this.$service.hrdc}/v1/en/console/crmView/rename`);
      this.api.setParams({
        id: viewId,
        name: viewName,
      });

      this.api.setCallbackCompleted(() => {
        this.$store.dispatch("showMessage", {
          message: "View Name Updated successfully.",
          messageType: "success",
          timeout: 2000,
        });
        this.renameEachViewDialog = false;
        this.$router.go();
      });
      this.api.fetch();
    },
    openViewNameDialog(viewId, viewName) {
      this.selectedViewId = viewId;
      this.selectedViewName = viewName;
      this.renameEachViewDialog = true;
    },
    deleteView(viewId) {
      // call API to delete the view
      this.api.setMethod(`POST`);
      this.api.setUrl(`${this.$service.hrdc}/v1/en/console/crmView/delete`);
      this.api.setParams({
        id: viewId,
      });

      this.api.setCallbackCompleted(() => {
        this.$store.dispatch("showMessage", {
          message: "View Deleted successfully.",
          messageType: "success",
          timeout: 2000,
        });
        // refresh the page to get the new view
        this.loadSpecificViewData("all");
        this.$router.go();
      });
      this.api.fetch();
    },
    setPrivateOrPublic(viewId, isPrivate) {
      this.api.setMethod(`POST`);
      this.api.setUrl(
        `${this.$service.hrdc}/v1/en/console/crmView/setPrivateOrPublic`
      );
      this.api.setParams({
        id: viewId,
        isPrivate: isPrivate,
      });

      this.api.setCallbackCompleted(() => {
        this.$store.dispatch("showMessage", {
          message: "View Changed successfully.",
          messageType: "success",
          timeout: 2000,
        });
        // refresh the page to get the new view
        this.$router.go();
      });
      this.api.fetch();
    },
    agreeCallback() {
      this.deleteView(this.selectedViewId);
    },
    disagreeCallback() {},
    openConfirmationDialog(viewId) {
      this.selectedViewId = viewId;
      this.$refs.confirmPopup.open({
        title: this.confirmationTitle,
        message: this.confirmationMessage,
        agree: () => {
          this.agreeCallback();
        },
        disagree: () => {
          this.disagreeCallback();
        },
      });
    },
    // IMPORTANT: Here is the place to set the url query.
    loadSpecificViewData(viewId) {
      this.$router
        .push({
          name: this.$route.name,
          params: {
            serviceKey: this.model.serviceKey,
            modelKey: this.model.key,
          },
          query: {
            viewId: viewId,
          },
        })
        .catch(() => {});
    },
  },
  created() {
    this.currentUserID = this.auth.uuid;
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rename-title {
  color: var(--app-text);
}

.dark-mode .rename-subtitle {
  color: var(--app-text-muted);
}

.dark-mode .rename-input >>> fieldset {
  border-color: var(--app-border-strong) !important;
  background-color: var(--app-card) !important;
}

.dark-mode .rename-input >>> input {
  color: var(--app-text);
}

.dark-mode .rename-btn--cancel {
  background: var(--app-card) !important;
  border: 1px solid var(--app-border-strong) !important;
}

.dark-mode .rename-btn--cancel:hover {
  background: var(--app-surface) !important;
}

/* >>> end auto-dark <<< */

/* === Views dropdown activator ===
   Written against the theme tokens, so one set of rules covers both themes:
   in light it resolves to the white-on-#f8fafc pair the strip already used, in
   dark to the charcoal ramp. The overrides are !important because layout.css
   paints every `.tb-view-area .theme--light.v-btn.v-btn--has-bg` #ffffff, which
   is the literal white that made this button glow in dark mode. */
.tb-view-area .tb-view-menu-btn.v-btn {
  float: left;
  height: 40px !important;
  min-width: 42px !important;
  padding: 0 11px !important;
  border-radius: 6px 6px 0 0 !important;
  background: var(--app-card) !important;
  border: 1px solid var(--app-border) !important;
  border-bottom: none !important;
  box-shadow: none !important;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.tb-view-area .tb-view-menu-btn.v-btn .v-icon {
  color: var(--app-text-muted) !important;
  font-size: 19px !important;
  transition: color 0.15s ease;
}

/* Vuetify's own overlay is a black wash — on charcoal it dims the button on
   hover instead of lifting it. Drop it and drive the states from the tokens. */
.tb-view-area .tb-view-menu-btn.v-btn::before {
  opacity: 0 !important;
}

.tb-view-area .tb-view-menu-btn.v-btn:hover {
  background: var(--app-elevated) !important;
}

.tb-view-area .tb-view-menu-btn.v-btn:hover .v-icon,
.tb-view-area .tb-view-menu-btn.v-btn[aria-expanded="true"] .v-icon {
  color: var(--app-accent) !important;
}

.tb-view-area .tb-view-menu-btn.v-btn[aria-expanded="true"] {
  background: var(--app-elevated) !important;
  border-color: var(--app-accent-border) !important;
}

/* Dark keeps the button one tier BELOW --app-card: the strip behind it is
   --app-page, and --app-card there is a ~7-point lightness jump where the
   light-mode pair it mirrors (#fff on #f8fafc) is only ~1.7. This mirrors the
   choice the hrdc/hrdc_event Index pages already make for the same button. */
.dark-mode .tb-view-area .tb-view-menu-btn.v-btn {
  background: var(--app-surface) !important;
}

.custom-save-btn {
  border-radius: 5px !important;
  height: 48px !important;
}
.as-private {
  margin: 0px auto;
}
.v-label {
  font-size: 12px !important;
}

/* === Rename View Dialog (HRDC modern theme) === */
.rename-card {
  border-radius: 20px !important;
  padding: 22px 28px 24px 28px;
  background: #eef0f4 !important;
  font-family: "Lato", sans-serif;
  text-align: center;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.18) !important;
}
.rename-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  margin-bottom: 6px;
}
.rename-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 22px;
}
.rename-input {
  font-family: "Lato", sans-serif;
  margin-bottom: 4px;
}
.rename-input >>> .v-input__slot {
  border-radius: 10px !important;
  background: transparent !important;
  min-height: 38px !important;
  height: 38px !important;
}
.rename-input >>> .v-text-field__slot {
  min-height: 38px !important;
}
.rename-input >>> fieldset {
  border-color: #cbd5e1 !important;
  background-color: #ffffff !important;
  border-radius: 10px !important;
  transition: border-color 0.2s ease;
}
.rename-input >>> fieldset legend {
  width: 0 !important;
  display: none !important;
}
.rename-input >>> .v-label {
  display: none !important;
}
.rename-input >>> .v-input--is-focused fieldset,
.rename-input.v-input--is-focused >>> fieldset {
  border-color: #0ea5e9 !important;
  border-width: 1.5px !important;
}
.rename-input >>> input {
  font-family: "Lato", sans-serif;
  font-size: 14px;
  color: #0f172a;
  padding: 6px 0 !important;
  max-height: 38px !important;
}
.rename-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: center;
}
.rename-btn {
  flex: 1;
  height: 42px !important;
  border-radius: 10px !important;
  font-family: "Lato", sans-serif;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
.rename-btn--cancel {
  background: #ffffff !important;
  color: #0ea5e9 !important;
  border: 1px solid #cbd5e1 !important;
}
.rename-btn--cancel:hover {
  background: #f8fafc !important;
  border-color: #0ea5e9 !important;
}
.rename-btn--confirm {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8) !important;
  color: #ffffff !important;
}
.rename-btn--confirm:hover {
  background: linear-gradient(135deg, #0284c7, #0ea5e9) !important;
  box-shadow: 0 6px 18px rgba(14, 165, 233, 0.35) !important;
}
</style>

<!-- The views dropdown panel is teleported to <body>, so it never carries this
     component's scope attribute — it has to be styled unscoped, keyed on the
     menu's content-class. Everything below resolves through the theme tokens,
     so light and dark come from the same rules. -->
<style>
.v-menu__content.tb-view-menu {
  border-radius: 12px !important;
  border: 1px solid var(--app-border);
  background: var(--app-card);
  box-shadow: 0 10px 30px var(--app-shadow), 0 2px 6px var(--app-shadow-soft) !important;
  overflow: hidden auto;
  max-height: 340px !important;
  min-width: 190px !important;
}

.v-menu__content.tb-view-menu .v-list {
  background: var(--app-card) !important;
  padding: 6px !important;
  font-family: "Lato", sans-serif;
}

.v-menu__content.tb-view-menu .tb-view-menu__label {
  padding: 4px 10px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-text-faint);
}

.v-menu__content.tb-view-menu .tb-view-menu__item {
  min-height: 34px !important;
  padding: 0 10px !important;
  border-radius: 8px !important;
  margin-bottom: 2px;
  transition: background 0.12s ease;
}

/* Vuetify's list-item overlay and ripple are both currentColor — which stays
   the light theme's near-black here, so on a charcoal panel "hover" darkened
   the row instead of highlighting it. Both are replaced by a real background. */
.v-menu__content.tb-view-menu .tb-view-menu__item::before {
  display: none !important;
}

.v-menu__content.tb-view-menu .tb-view-menu__item .v-ripple__container {
  display: none !important;
}

.v-menu__content.tb-view-menu .tb-view-menu__item .v-list-item__title {
  font-size: 13px !important;
  font-weight: 500;
  color: var(--app-text) !important;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-menu__content.tb-view-menu .tb-view-menu__item .v-icon {
  color: var(--app-text-faint) !important;
  margin-right: 6px;
}

.v-menu__content.tb-view-menu .tb-view-menu__item:hover {
  background: var(--app-hover);
}

.v-menu__content.tb-view-menu .tb-view-menu__item--active {
  background: var(--app-accent-soft);
}

.v-menu__content.tb-view-menu .tb-view-menu__item--active .v-list-item__title {
  color: var(--app-accent-strong) !important;
  font-weight: 700;
}

.v-menu__content.tb-view-menu .tb-view-menu__item--active .v-icon {
  color: var(--app-accent) !important;
}

/* Scrollbar, for the pages that accumulate a long view list. */
.v-menu__content.tb-view-menu::-webkit-scrollbar {
  width: 8px;
}

.v-menu__content.tb-view-menu::-webkit-scrollbar-thumb {
  background: var(--app-border-strong);
  border-radius: 4px;
}
</style>
